import { Formik, Form } from 'formik';
import { Text } from '@rsos/base-components';
import { Box, Button, Select } from '@rsos/components/capstone/base';
import { useFlag } from '@rsos/flags';
import type { EtlTransportResponse } from '@rsos/reduxDataStore/src/features/resourceRequests';
import {
  Label,
  ButtonContainer,
  TextArea,
  Loader,
} from '../AirAmbulanceForm/AirAmbulanceFormStandalone.styles';
import LandingZoneSelect from '../AirAmbulanceForm/components/LandingZoneSelect';
import {
  REQUEST_REASONS,
  FORM_FIELDS,
  DEFAULT_BUTTON_OPTIONS,
} from '../AirAmbulanceForm/constants';
import { LabeledButtonGroup } from './LabeledButtonGroup';
import { LabeledInput } from './LabeledInput';
import { useEditFormSubmit } from './hooks/useEditFormSubmit';
import {
  useEditFormValues,
  type EditFormValues,
} from './hooks/useEditFormValues';

interface AirAmbulanceEtlEditFormProps {
  transport: EtlTransportResponse;
}

function AirAmbulanceEtlEditForm({
  transport,
}: AirAmbulanceEtlEditFormProps): JSX.Element {
  const isGmrLandingZoneEnabled = useFlag('gmr_landing_zone');
  const { handleSubmit, handleCancel, isLoading } =
    useEditFormSubmit(transport);
  const initialValues = useEditFormValues(transport);

  const editableButtonFields = FORM_FIELDS.filter(
    f => f.field !== 'launch_type',
  );
  const launchTypeField = FORM_FIELDS.find(f => f.field === 'launch_type');

  function notEditableTooltip(fieldName: string): string {
    return `${fieldName} is set by the original ETL request and cannot be changed.`;
  }

  return (
    <Box>
      <Formik<EditFormValues>
        enableReinitialize={false}
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        {({ values, handleChange, handleBlur, setFieldValue }) => (
          <Form>
            {isGmrLandingZoneEnabled && (
              <div id="landing-zone-select-placeholder" hidden>
                {
                  // TODO: Something needs to be done here with landing zone,
                  //   but I don't know how to test or what to pass to
                  //   selectedLandingZone 🤷‍♂️ See
                  //   src/components/AirAmbulanceForm/AirAmbulanceForm/components/LocationSection.tsx
                  //   for the midas implementation.
                }
                <LandingZoneSelect
                  setFieldValue={setFieldValue}
                  handleChange={handleChange}
                  handleLandingZoneSelect={() => undefined}
                  selectedLandingZone={null}
                />
              </div>
            )}

            <LabeledInput
              label="Pickup Location"
              name="pickup_location"
              value={values.pickup_location}
              setFieldValue={setFieldValue}
              onChange={handleChange}
              onBlur={handleBlur}
            />

            <Box mx="5px">
              <LabeledInput
                label="Pickup Lat/Long*"
                name="pickup_coordinates"
                value={values.pickup_coordinates}
                setFieldValue={setFieldValue}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="latitude, longitude"
              />

              <LabeledInput
                label="Pickup Location Details"
                name="pickup_details"
                value={values.pickup_details}
                setFieldValue={setFieldValue}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Pickup Location Details"
              />

              <LabeledInput
                label="Ground Contact Radio Channel & Unit Name*"
                name="ground_contact"
                value={values.ground_contact}
                setFieldValue={setFieldValue}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Ground contact"
              />

              <Box pb={3} title={notEditableTooltip('Incident/Patient Info')}>
                <Label isDisabled>
                  <Text size="small" color="primaryText">
                    Incident/Patient Info*
                  </Text>
                </Label>
                <TextArea
                  name="patient_info"
                  value={values.patient_info}
                  disabled
                  rows={3}
                  style={{ opacity: 0.5, cursor: 'not-allowed' }}
                />
              </Box>

              <Box pb={3} title={notEditableTooltip('Request Reason')}>
                <Label isDisabled>
                  <Text size="small" color="primaryText">
                    Request Reason
                  </Text>
                </Label>
                <Select
                  name="request_reason"
                  options={REQUEST_REASONS}
                  placeholder="Please select"
                  isClearable={false}
                  isSearchable={false}
                  value={values.request_reason}
                  isDisabled
                />
              </Box>

              {launchTypeField && (
                <LabeledButtonGroup
                  label={launchTypeField.label}
                  options={launchTypeField.options || DEFAULT_BUTTON_OPTIONS}
                  selectedValue={values.launch_type}
                  disabled
                  title={notEditableTooltip('Launch Type')}
                />
              )}

              {editableButtonFields.map(
                ({ label, field, options = DEFAULT_BUTTON_OPTIONS }) => (
                  <LabeledButtonGroup
                    key={field}
                    label={label}
                    options={options}
                    selectedValue={values[field]}
                    onSelect={option => setFieldValue(field, option)}
                  />
                ),
              )}

              <Box pt={4}>
                <ButtonContainer horizontal>
                  <Button
                    grow
                    btnType="tertiary"
                    type="button"
                    onClick={handleCancel}
                    disabled={isLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    grow
                    type="submit"
                    btnType="primary"
                    disabled={isLoading}
                  >
                    {isLoading ? <Loader /> : 'Save Changes'}
                  </Button>
                </ButtonContainer>
              </Box>
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  );
}

export default AirAmbulanceEtlEditForm;
